import {
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from 'react'

import { ChatContext } from '@app/context'
import type { Listing } from '@app/models'

import {
  AttachmentListingsContext,
  getListingSemaphore,
  getMessageListingData,
  fetchingAttachmentListingIds,
  missingAttachmentListingIds,
  sharedFetchedListings,
} from './constants'
import { loadAttachmentListings } from './loadAttachmentListings'
import type { AttachmentListingsProviderProps } from './types'
import { useMainBuyerLocale } from '../../../Reply/useMainBuyerLocale'

export const AttachmentListingsProvider = ({
  conversation,
  children,
}: AttachmentListingsProviderProps) => {
  const { state } = use(ChatContext)
  const mainBuyerLocale = useMainBuyerLocale(conversation)
  const buyerCulture = mainBuyerLocale.culture
  const buyerLanguage = mainBuyerLocale.language
  const conversationMessages = conversation.messages
  const isUnmountedRef = useRef(false)

  const [fetchedListingsState, setFetchedListingsState] = useState<
    Map<number, Listing>
  >(() => new Map(sharedFetchedListings))

  const setFetchedListings = useCallback(
    (updater: SetStateAction<Map<number, Listing>>) => {
      setFetchedListingsState((prev) => {
        const prevClone = new Map(prev)
        const next =
          typeof updater === 'function'
            ? updater(prevClone)
            : updater

        next.forEach((listing, listingId) => {
          sharedFetchedListings.set(listingId, listing)
          missingAttachmentListingIds.delete(listingId)
        })

        return new Map(sharedFetchedListings)
      })
    },
    [setFetchedListingsState]
  )
  const fetchedListings = fetchedListingsState

  useEffect(() => {
    setFetchedListings(new Map(sharedFetchedListings))
  }, [conversation.id, setFetchedListings])

  useEffect(() => {
    isUnmountedRef.current = false

    return () => {
      isUnmountedRef.current = true
    }
  }, [])

  useEffect(() => {
    if (!conversationMessages.length) {
      return
    }

    const idsToLoad = new Set<number>()

    conversationMessages.forEach((message) => {
      const listingIds = getMessageListingData(message)

      if (!listingIds.length) {
        return
      }
      listingIds.forEach((listingId) => {
        if (state.listings.has(listingId)) {
          missingAttachmentListingIds.delete(listingId)
          return
        }

        if (sharedFetchedListings.has(listingId)) {
          return
        }

        if (fetchingAttachmentListingIds.has(listingId)) {
          return
        }

        if (missingAttachmentListingIds.has(listingId)) {
          return
        }

        if (fetchedListings.has(listingId)) {
          return
        }

        idsToLoad.add(listingId)
      })
    })

    if (!idsToLoad.size) {
      return
    }

    const trackedListingIds = [...idsToLoad].sort((a, b) => a - b)
    trackedListingIds.forEach((listingId) => {
      fetchingAttachmentListingIds.add(listingId)
    })
    void loadAttachmentListings({
      trackedListingIds,
      buyerCulture,
      buyerLanguage,
      fetchedListings,
      fetchingAttachmentListingIds,
      setFetchedListings,
      stateListings: state.listings,
      getListingSemaphore,
      isCancelled: () => isUnmountedRef.current,
    })
  }, [
    buyerCulture,
    buyerLanguage,
    conversationMessages,
    fetchedListings,
    setFetchedListings,
    state.listings,
  ])

  const value = useMemo(() => ({ fetchedListings }), [fetchedListings])

  return (
    <AttachmentListingsContext.Provider value={value}>
      {children}
    </AttachmentListingsContext.Provider>
  )
}
